// Auth Manager
// Authentication state management and handlers
// Soporta dos tipos de auth: email/password (staff) y anónima (clientes)

import {
    signInWithEmailAndPassword,
    onAuthStateChanged,
    signOut
} from "firebase/auth";
import { auth, signInAnonymously } from '../config/firebase-config.js';
import { getSellerInfo, endShift } from '../services/seller-service.js';
import stateManager from './state-manager.js';

/**
 * Check if a Firebase user is a staff member (not anonymous)
 * @param {object} user - Firebase Auth user object
 * @returns {boolean} true if user is staff (email/password auth)
 */
export function isStaffUser(user) {
    if (!user) return false;
    return !user.isAnonymous;
}

/**
 * Initialize authentication listener
 * Handles both anonymous (customer) and staff (email/password) users
 * When no user exists, auto-signs-in anonymously
 * @param {function} onAuthChange - Callback when auth state changes
 */
export function initAuthListener(onAuthChange) {
    onAuthStateChanged(auth, async (user) => {
        stateManager.setCurrentUser(user);

        if (user) {
            if (isStaffUser(user)) {
                // Staff user (email/password login)
                const sellerInfo = await getSellerInfo(user.uid);

                if (sellerInfo) {
                    stateManager.setShiftActive(sellerInfo.isOnline || false);
                    stateManager.setSellerPaused(sellerInfo.isPaused || false);
                }

                if (onAuthChange) {
                    onAuthChange(user, sellerInfo);
                }
            } else {
                // Anonymous user (customer)
                if (onAuthChange) {
                    onAuthChange(user, null);
                }
            }
        } else {
            // No user — auto sign-in anonymously
            stateManager.setShiftActive(false);
            stateManager.setSellerPaused(false);

            try {
                await signInAnonymously(auth);
                // onAuthStateChanged will fire again with the anonymous user
            } catch (error) {
                console.error('Error signing in anonymously:', error);
                if (onAuthChange) {
                    onAuthChange(null, null);
                }
            }
        }
    });
}

/**
 * Sign in with email and password (staff login)
 * If current user is anonymous, Firebase automatically replaces the session
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise} Firebase auth result
 */
export async function signIn(email, password) {
    try {
        return await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
        console.error("Error signing in:", error);
        throw error;
    }
}

/**
 * Sign out current user
 * Ends shift for staff users before signing out
 * After sign-out, the auth listener auto-signs-in anonymously
 */
export async function logout() {
    const currentUser = stateManager.getState('currentUser');

    if (currentUser && isStaffUser(currentUser)) {
        await endShift(currentUser.uid);
    }

    await signOut(auth);
}

/**
 * Get current authenticated user
 * @returns {object|null} Current user or null
 */
export function getCurrentUser() {
    return stateManager.getState('currentUser');
}
